import React, { useState, useCallback } from "react";
import { Container, Title, Text, TextInput, LoadingOverlay, Alert, Button, ActionIcon, Group, Space } from '@mantine/core';
import { IconPlus, IconPencil, IconTrash } from "@tabler/icons";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-location";
import * as App from "./../model/App";
import { ClientEditModal, ClientsTable, ClientsTableColumns } from "./../components";
import { useDeleteConfirmation } from "./../hooks";

export default function ClientsScreen() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { isLoading, isError, data } = useQuery(["clients"], async () => { 
        const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
        return App.Firebase.Read.getClients(userID); 
    });
    const [isShowingEditModal, setIsShowingEditModal] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");

    const createClient = useMutation(async (agent: string) => { 
            const client: App.Data.App.IClient = { agent, attorney: "", email: "", country: "" };
            const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
            return App.Firebase.Write.createClient(userID, client); 
        }, { onSuccess: () => { queryClient.invalidateQueries(["clients"]); } }
    );

    const deleteClient = useMutation(async (key: string) => {
            const userID = await App.Firebase.Auth.getEffectiveUserID() || "";
            return App.Firebase.Write.deleteClient(userID, key);
        }, { onSuccess: () => { queryClient.invalidateQueries(["clients"]); } }
    );

    // Delete confirmation...
    const { setShowDeleteConfirmation, deleteConfirmationDialog } = useDeleteConfirmation({ 
        title: "Delete",
        message: "Are you sure you want to delete this client? This action can NOT be undone.",
        onDeleteConfirm: deleteClient.mutate
    });

    const onEditClientClick = useCallback((clientId: string) => {
        navigate({ to: `/client/${clientId}` });
    }, [navigate]);

    // Build data for the table...
    const tmData = React.useMemo((): ClientsTableColumns[] => {
        if (!data) { return []; }

        const clients = Object.keys(data)
            .map((key) => { return { key: key, client: data[key] } })
            .filter((c) => {
                if (searchText.length === 0) { return true; }
                if (c.client.agent.toLowerCase().includes(searchText.toLowerCase())) { return true; }
                if (c.client.attorney.toLowerCase().includes(searchText.toLowerCase())) { return true; }
                return false;
            });

        return clients.map(c => {
            const key = c.key;
            const client: App.Data.App.IClient = c.client;
            return {
                edit: <ActionIcon onClick={() => { onEditClientClick(key) }}><IconPencil /></ActionIcon>,
                delete: <ActionIcon onClick={() => { setShowDeleteConfirmation(true, key); }}><IconTrash /></ActionIcon>,
                agent: client.agent,
                attorney: client.attorney
            };
        });
    }, [data, searchText, onEditClientClick, setShowDeleteConfirmation]);

    return (
        <Container ml={0} mr={30} style={{ maxWidth: "100%" }}>
            <Group position="apart">
                <Title>Clients</Title>
                <Button leftIcon={<IconPlus size={14} />} onClick={() => { setIsShowingEditModal(true); }}>New Client</Button>
            </Group>
            
            <LoadingOverlay visible={isLoading || createClient.isLoading} />
            {isError && <Alert color="red" title="Error">Error loading data</Alert>}
            {createClient.isError && <Alert color="red" title="Error">Error creating client</Alert>}
            {deleteConfirmationDialog}

            <ClientEditModal visible={isShowingEditModal} onClose={() => { setIsShowingEditModal(false); }} onSave={(title: string) => {
                createClient.mutate(title);
                setIsShowingEditModal(false);
            }} />            

            <Space h="md" />
            {data && Object.keys(data).length === 0 && <Text>No clients found</Text>}
            {data && Object.keys(data).length > 0 && <>
                <Group position="right">
                    <TextInput placeholder="Search" value={searchText} onChange={(event) => { setSearchText(event.target.value); }}  />
                </Group>
                <ClientsTable data={tmData} />
                </>
            }
        </Container>
    );
}